import { createSlice } from '@reduxjs/toolkit';

/**
 * The vendor book — who the shop owes, and what for.
 *
 * The same shape as the customer slice, because the two books work the same
 * way. Ledgers are keyed by vendor id and filled lazily: a shop with forty
 * suppliers should not pull forty ledgers to show one.
 *
 * Note that the EXPENSES screen also holds a `vendors` list, fetched only when
 * somebody files an expense on invoice. The two are deliberately separate:
 * that one is a picker that needs names and ids, this one is a book that needs
 * balances and has to be refetched whenever a balance moves. Sharing them
 * would mean the expense picker quietly invalidating this screen, or this
 * screen serving the picker a list it had already let go stale.
 *
 * Wiped on logout by the reset in store.js.
 */
const initialState = {
  vendors: [],
  isVendorDataFetched: false,
  transactionsByVendor: {},
};

export const vendorSlice = createSlice({
  name: 'vendors',
  initialState,
  reducers: {
    setVendors: (state, action) => {
      state.vendors = action.payload || [];
    },

    setVendorDataFetched: (state, action) => {
      state.isVendorDataFetched = action.payload;
      // Invalidating the list invalidates the ledgers with it — they were read
      // alongside those balances, and an expense that moved one has almost
      // certainly moved the other.
      if (!action.payload) state.transactionsByVendor = {};
    },

    addVendor: (state, action) => {
      if (action.payload) state.vendors.unshift(action.payload);
    },

    updateVendor: (state, action) => {
      const { _id, updatedVendor } = action.payload;
      const index = state.vendors.findIndex((vendor) => vendor._id === _id);
      if (index !== -1) {
        state.vendors[index] = { ...state.vendors[index], ...updatedVendor };
      }
    },

    setVendorTransactions: (state, action) => {
      const { vendorId, transactions } = action.payload;
      if (!vendorId) return;
      state.transactionsByVendor[vendorId] = transactions || [];
    },

    /**
     * A new line, and the balance it moves.
     *
     * The balance is adjusted here rather than refetched so the list beside
     * the ledger agrees with it immediately. The server has already applied
     * the same arithmetic — debit up, credit down — inside its own session.
     */
    addVendorTransaction: (state, action) => {
      const { vendorId, transaction } = action.payload;
      if (!vendorId || !transaction) return;

      const ledger = state.transactionsByVendor[vendorId] || [];
      state.transactionsByVendor[vendorId] = [...ledger, transaction];

      const index = state.vendors.findIndex((vendor) => vendor._id === vendorId);
      if (index !== -1) {
        const moved =
          Number(transaction.debit || 0) - Number(transaction.credit || 0);
        state.vendors[index].balance =
          Math.round((Number(state.vendors[index].balance || 0) + moved) * 100) / 100;
      }
    },

    replaceVendorTransaction: (state, action) => {
      const { vendorId, transaction } = action.payload;
      if (!vendorId || !transaction) return;

      const ledger = state.transactionsByVendor[vendorId] || [];
      const previous = ledger.find((row) => row._id === transaction._id);

      state.transactionsByVendor[vendorId] = ledger.map((row) =>
        row._id === transaction._id ? transaction : row
      );

      // Only the DIFFERENCE moves the balance, which is what the server does
      // too — a bill corrected from 50 to 40 moves it by 10, not by 40.
      const index = state.vendors.findIndex((vendor) => vendor._id === vendorId);
      if (index !== -1 && previous) {
        const before = Number(previous.debit || 0) - Number(previous.credit || 0);
        const after = Number(transaction.debit || 0) - Number(transaction.credit || 0);
        state.vendors[index].balance =
          Math.round((Number(state.vendors[index].balance || 0) + (after - before)) * 100) /
          100;
      }
    },

    removeVendorTransaction: (state, action) => {
      const { vendorId, transactionId } = action.payload;
      if (!vendorId || !transactionId) return;

      const ledger = state.transactionsByVendor[vendorId] || [];
      const removed = ledger.find((row) => row._id === transactionId);

      state.transactionsByVendor[vendorId] = ledger.filter(
        (row) => row._id !== transactionId
      );

      const index = state.vendors.findIndex((vendor) => vendor._id === vendorId);
      if (index !== -1 && removed) {
        const moved = Number(removed.debit || 0) - Number(removed.credit || 0);
        state.vendors[index].balance =
          Math.round((Number(state.vendors[index].balance || 0) - moved) * 100) / 100;
      }
    },
  },
});

export const {
  setVendors,
  setVendorDataFetched,
  addVendor,
  updateVendor,
  setVendorTransactions,
  addVendorTransaction,
  replaceVendorTransaction,
  removeVendorTransaction,
} = vendorSlice.actions;

export default vendorSlice.reducer;
